import { type ReactNode, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { RiExpandDiagonalLine } from '@remixicon/react';

import { useRouteRef } from '@backstage/core-plugin-api';

import type { CsrPlayerElement } from '@spotify-internal/csr-player';
import {
  ButtonIcon,
  Flex,
  ProgressCircle,
  Text,
} from '@spotify-internal/encore-web';
import {
  CAROUSEL_ICON_SIZE,
  Carousel,
  extractLastNameComponent,
} from '@spotify/confidence-core-react';
import {
  RecordingPlayer,
  formatRecordingTime,
  useRecording,
} from '@spotify/confidence-recordings-react';

import { recordingRouteRef } from '../routes';
import { getRecordingDownload } from '../utils';
import { type RecordingHit, getRecordingLabel } from './types';

import styles from './RecordingsResult.module.css';

type RecordingPreviewCardProps = {
  recording: RecordingHit;
  onOpenFullView: () => void;
  onPlayerReady: (player: CsrPlayerElement) => void;
};

const RecordingPreviewCard = ({
  recording,
  onOpenFullView,
  onPlayerReady,
}: RecordingPreviewCardProps) => {
  const [playerError, setPlayerError] = useState<Error | undefined>();
  const {
    recording: recordingDetails,
    isLoading,
    error: recordingError,
  } = useRecording({
    name: recording.name,
    fetchPolicy: 'network-only',
  });
  const { downloadUri, downloadError } = getRecordingDownload(recordingDetails);
  const error = recordingError ?? downloadError ?? playerError;
  const createTime = recordingDetails?.createTime
    ? formatRecordingTime(recordingDetails.createTime)
    : undefined;
  let playerContent: ReactNode;

  if (isLoading) {
    playerContent = (
      <div className={styles.playerStatus}>
        <ProgressCircle size="small" aria-label="Loading recording preview" />
      </div>
    );
  } else if (error || !downloadUri) {
    playerContent = (
      <div className={styles.playerStatus}>
        <Text as="p" variant="bodySmall" semanticColor="textNegative">
          Unable to load recording preview
        </Text>
      </div>
    );
  } else {
    playerContent = (
      <RecordingPlayer
        src={downloadUri}
        variant="simplified"
        autoplay={false}
        initialPageUrl={recordingDetails?.routing?.entryPage ?? undefined}
        onPlayerReady={onPlayerReady}
        onError={setPlayerError}
      />
    );
  }

  return (
    <article
      aria-label={getRecordingLabel(recording)}
      className={styles.recordingCard}
      data-track-scope="region.recording-preview"
      data-track-data-recording={recording.name}
    >
      <Flex direction="column">
        <header className={styles.recordingHeader}>
          <Flex alignItems="center" justifyContent="space-between">
            <Flex direction="column">
              <Text as="p" variant="bodyMediumBold" lineClamp={1}>
                {getRecordingLabel(recording)}
              </Text>
              {createTime && (
                <Text
                  as="p"
                  variant="marginal"
                  semanticColor="textSubdued"
                  lineClamp={1}
                >
                  {createTime}
                </Text>
              )}
            </Flex>
            <ButtonIcon
              variant="tertiary"
              size="small"
              aria-label="Open full view"
              onClick={onOpenFullView}
              data-track-id="button.open-full-view"
              data-track-action="navigate"
            >
              <RiExpandDiagonalLine size={CAROUSEL_ICON_SIZE} aria-hidden />
            </ButtonIcon>
          </Flex>
        </header>
        <div className={styles.playerContainer}>{playerContent}</div>
      </Flex>
    </article>
  );
};

export const RecordingsResult = ({
  recordings,
}: {
  recordings: RecordingHit[];
}) => {
  const navigate = useNavigate();
  const recordingRoute = useRouteRef(recordingRouteRef);
  const playersRef = useRef(new Map<string, CsrPlayerElement>());

  if (recordings.length === 0) {
    return null;
  }

  const openFullView = (recording: RecordingHit) => {
    const recordingId =
      extractLastNameComponent(recording.name) ?? recording.name;
    const recordingHref = recordingRoute({ id: recordingId });
    const currentTime = playersRef.current.get(recording.name)?.currentTime;

    navigate(currentTime ? `${recordingHref}?t=${currentTime}` : recordingHref);
  };

  return (
    <Carousel
      className={styles.carousel}
      ariaLabel="Recording carousel"
      itemName="recording"
    >
      {recordings.map(carouselRecording => (
        <RecordingPreviewCard
          key={carouselRecording.name}
          recording={carouselRecording}
          onOpenFullView={() => openFullView(carouselRecording)}
          onPlayerReady={player => {
            playersRef.current.set(carouselRecording.name, player);
          }}
        />
      ))}
    </Carousel>
  );
};
